import { FC, Fragment, useEffect, useState } from 'react';

// API
import apibridge, { AssessmentDashboardParamsType } from '../../../apibridge';

// REDUX
import { useAppDispatch } from '../../../store/slice';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';

// PACKAGES
import { Spinner } from 'react-bootstrap';

// UTILS
import { guid, inlineWait } from '../../../libs/utils';

// COMPONENTS
import ProgressPie from './ProgressPie';
import SvgMask from '../../_Helpers/SvgMask';
import ProgressChart from './ProgressChart';
import DelayedFadeIn from '../../_Helpers/DelayedFadeIn';
import BenchmarkChart from './BenchmarkChart';
import StageMasteryChart from './StageMasteryChart';

// TYPES
import { AssessmentBenchmarkLevel, AssessmentDashboardDashboardInfo } from '../../../api/models';
import { AssessmentResultTypeFilter, TabStatus } from '../../../pages/AssessmentAdmin/AssessmentAdmin';
import { SortOptionsType } from '../../TitleSection/SortDropdown';

interface AssessmentDashboardProps {
	assessmentDashboardParams: AssessmentDashboardParamsType;
	setBenchmarkLevel: React.Dispatch<React.SetStateAction<AssessmentBenchmarkLevel | undefined>>;
	setAssessmentTypeFilter: React.Dispatch<React.SetStateAction<AssessmentResultTypeFilter>>;
	setTabStatus: React.Dispatch<React.SetStateAction<TabStatus>>;
	recordQueryParams: (tabView: TabStatus) => void;
	selectedViewOption: SortOptionsType;
}

const AssessmentDashboard: FC<AssessmentDashboardProps> = ({
	assessmentDashboardParams,
	setBenchmarkLevel,
	setAssessmentTypeFilter,
	setTabStatus,
	recordQueryParams,
	selectedViewOption
}) => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isReloading, setIsReloading] = useState(false);
	const [dashboardData, setDashboardData] = useState<AssessmentDashboardDashboardInfo>();

	const handleBenchmarkClick = async ({
		benchmark,
		assessmentType
	}: {
		benchmark: AssessmentBenchmarkLevel;
		assessmentType: AssessmentResultTypeFilter;
	}) => {
		setIsReloading(true);
		setBenchmarkLevel(benchmark);
		setAssessmentTypeFilter(assessmentType);

		// give the other components time to update before switching tabs, or else multiple requests are made
		await inlineWait(50);
		recordQueryParams('Results');
		setTabStatus('Results');
	};

	const getAssessmentDashboardData = async () => {
		setIsReloading(true);

		const response = await apibridge.getAssessmentDashboard({
			_class: assessmentDashboardParams._class || '',
			assessmentPeriodId: assessmentDashboardParams.assessmentPeriodId || ''
		});

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setDashboardData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Dashboard error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
		setIsReloading(false);
	};

	useEffect(() => {
		// make sure we only fetch the data once
		if (!isReloading) {
			getAssessmentDashboardData();
		}
	}, [assessmentDashboardParams]); // eslint-disable-line react-hooks/exhaustive-deps

	const { benchmarkReading, benchmarkSpelling, stageMasteryReading, stageMasterySpelling } = dashboardData || {};

	return (
		<div className="mt-5">
			{isLoading ? (
				<div className="d-flex align-items-center justify-content-center position-relative w-100">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					{/* ASSESSMENT DETAILS CHARTS */}
					<div className="d-flex gap-3">
						<article className={`assessment-details-card ${isReloading ? 'opacity-75' : ''}`}>
							<div className="d-flex flex-column align-items-center justify-content-center p-4">
								<p className="h3 mb-4 text-black">
									<strong>Reading</strong>
								</p>

								<ProgressChart
									size={200}
									strokeWidth={10}
									progress={{
										aboveBenchmark: dashboardData?.reading?.aboveBenchmarkPercentage,
										atBenchmark: dashboardData?.reading?.atBenchmarkPercentage,
										belowBenchmark: dashboardData?.reading?.belowBenchmarkPercentage,
										notAssessed: dashboardData?.reading?.notAssessedPercentage
									}}
									iconPath="/svg/reading-progress-icon.svg"
								/>
							</div>

							{/* Above Benchmark */}
							<div className="card-divider mt-3"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'AboveBenchmark', assessmentType: 'Reading' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>Above LLLL benchmark</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.reading?.aboveBenchmarkCount}/{dashboardData?.reading?.totalCount}{' '}
										{dashboardData?.reading?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie percentage={dashboardData?.reading?.aboveBenchmarkPercentage} />
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>

							{/* At Benchmark */}
							<div className="card-divider"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'AtBenchmark', assessmentType: 'Reading' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>At LLLL benchmark</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.reading?.atBenchmarkCount}/{dashboardData?.reading?.totalCount}{' '}
										{dashboardData?.reading?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie
										percentage={dashboardData?.reading?.atBenchmarkPercentage}
										progressColor="#00B6F1"
										backgroundColor="rgba(0, 182, 241,0.1)"
									/>
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>

							{/* Below Benchmark */}
							<div className="card-divider"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'BelowBenchmark', assessmentType: 'Reading' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>Below LLLL benchmark</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.reading?.belowBenchmarkCount}/{dashboardData?.reading?.totalCount}{' '}
										{dashboardData?.reading?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie
										percentage={dashboardData?.reading?.belowBenchmarkPercentage}
										progressColor="#FCB043"
										backgroundColor="rgba(252, 176, 67,0.1)"
									/>
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>

							{/* Not Assessed */}
							<div className="card-divider"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'NotAssessed', assessmentType: 'Reading' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>Not assessed</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.reading?.notAssessedCount}/{dashboardData?.reading?.totalCount}{' '}
										{dashboardData?.reading?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie
										percentage={dashboardData?.reading?.notAssessedPercentage}
										progressColor="#B1B3B5"
										backgroundColor="rgba(177, 179, 181,0.1)"
									/>
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>
						</article>

						<article className={`assessment-details-card ${isReloading ? 'opacity-75' : ''}`}>
							<div className="d-flex flex-column align-items-center justify-content-center p-4">
								<p className="h3 mb-4 text-black">
									<strong>Spelling</strong>
								</p>

								<ProgressChart
									size={200}
									strokeWidth={10}
									progress={{
										aboveBenchmark: dashboardData?.spelling?.aboveBenchmarkPercentage,
										atBenchmark: dashboardData?.spelling?.atBenchmarkPercentage,
										belowBenchmark: dashboardData?.spelling?.belowBenchmarkPercentage,
										notAssessed: dashboardData?.spelling?.notAssessedPercentage
									}}
									iconPath="/svg/spelling-progress-icon.svg"
								/>
							</div>

							{/* Above Benchmark */}
							<div className="card-divider mt-3"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'AboveBenchmark', assessmentType: 'Spelling' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>Above LLLL benchmark</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.spelling?.aboveBenchmarkCount}/{dashboardData?.spelling?.totalCount}{' '}
										{dashboardData?.spelling?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie percentage={dashboardData?.spelling?.aboveBenchmarkPercentage} />
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>

							{/* At Benchmark */}
							<div className="card-divider"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'AtBenchmark', assessmentType: 'Spelling' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>At LLLL benchmark</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.spelling?.atBenchmarkCount}/{dashboardData?.spelling?.totalCount}{' '}
										{dashboardData?.spelling?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie
										percentage={dashboardData?.spelling?.atBenchmarkPercentage}
										progressColor="#00B6F1"
										backgroundColor="rgba(0, 182, 241,0.1)"
									/>
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>

							{/* Below Benchmark */}
							<div className="card-divider"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'BelowBenchmark', assessmentType: 'Spelling' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>Below LLLL benchmark</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.spelling?.belowBenchmarkCount}/{dashboardData?.spelling?.totalCount}{' '}
										{dashboardData?.spelling?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie
										percentage={dashboardData?.spelling?.belowBenchmarkPercentage}
										progressColor="#FCB043"
										backgroundColor="rgba(252, 176, 67,0.1)"
									/>
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>

							{/* Not Assessed */}
							<div className="card-divider"></div>

							<button
								className="btn btn-result-row d-flex justify-content-between p-4"
								onClick={() => handleBenchmarkClick({ benchmark: 'NotAssessed', assessmentType: 'Spelling' })}
							>
								<div className="d-flex flex-column justify-content-center align-items-start">
									<p className="mb-0 body-small text-shades-800">
										<strong>Not assessed</strong>
									</p>
									<p className="mb-0 fw-semibold body-small text-shades-600">
										{dashboardData?.spelling?.notAssessedCount}/{dashboardData?.spelling?.totalCount}{' '}
										{dashboardData?.spelling?.totalCount === 1 ? 'student' : 'students'}
									</p>
								</div>

								<div className="d-flex align-items-center justify-content-end gap-3">
									<ProgressPie
										percentage={dashboardData?.spelling?.notAssessedPercentage}
										progressColor="#B1B3B5"
										backgroundColor="rgba(177, 179, 181,0.1)"
									/>
									<SvgMask path={'/svg/chevron-right.svg'} width={24} height={24} className="" />
								</div>
							</button>
						</article>
					</div>

					{/* BENCHMARK CHARTS */}
					<div className="d-flex flex-column flex-lg-row gap-3 mt-3">
						<BenchmarkChart
							title="LLLL Reading Benchmarks by Year Level"
							yearLevels={benchmarkReading?.yearLevels}
							selectedViewOption={selectedViewOption}
						/>

						<BenchmarkChart
							title="LLLL Spelling Benchmarks by Year Level"
							yearLevels={benchmarkSpelling?.yearLevels}
							selectedViewOption={selectedViewOption}
						/>
					</div>

					{/* MASTERY CHARTS */}
					<div className="d-flex gap-3 mt-3">
						<StageMasteryChart title="Reading by Stage Mastery" stageMastery={stageMasteryReading?.stages} />

						<StageMasteryChart title="Spelling by Stage Mastery" stageMastery={stageMasterySpelling?.stages} />
					</div>
				</>
			)}
		</div>
	);
};

export default AssessmentDashboard;
